import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { useKeenSlider } from "keen-slider/react";

type DotsProps = {
  count: number;
  active: number;
};

const Dots = ({ count, active }: DotsProps) => {
  return (
    <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-0.5 rounded-full bg-black/25 px-0.5 py-0.25 backdrop-blur">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={clsx(
            "m-0.5 size-1 shrink-0 rounded-full transition-transform",
            {
              "bg-white/25": index !== active,
              "scale-150 bg-white": index === active,
            },
          )}
        />
      ))}
    </div>
  );
};

type Props = {
  title?: string;
  subtitle?: string;
  tags?: ReactNode;
  imageRatio?: "7/5" | "3/2";
  imagesUrls: string[];
};

export const ImageCarousel = ({
  title,
  subtitle,
  tags,
  imageRatio = "7/5",
  imagesUrls,
}: Props) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    slides: {
      perView: 1,
      spacing: 8,
    },
    renderMode: "performance",
    slideChanged(slider) {
      setActiveSlide(slider.track.details.rel);
    },
  });

  return (
    <div className="relative">
      <div ref={sliderRef} className="keen-slider overflow-clip rounded-t-2xl">
        {imagesUrls.map((imageUrl) => (
          <div
            key={imageUrl}
            className={clsx("full-w keen-slider__slide bg-cover bg-center", {
              "aspect-7/5": imageRatio === "7/5",
              "aspect-3/2": imageRatio === "3/2",
            })}
            style={{
              backgroundImage: `${title || subtitle ? "linear-gradient(to top, var(--color-surface-accent-stronger) 10%, transparent 50%), " : ""}url('${imageUrl}')`,
            }}
          />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 flex flex-col justify-between gap-4 p-2">
        <div>
          {tags ? <div className="flex flex-wrap gap-1">{tags}</div> : null}
        </div>
        <div className="px-2 pb-4.5 text-text-inverted">
          <div className="line-clamp-2 text-xl font-semibold">{title}</div>
          <div className="line-clamp-2 text-sm">{subtitle}</div>
        </div>
      </div>

      {imagesUrls.length > 1 ? (
        <Dots count={imagesUrls.length} active={activeSlide} />
      ) : null}
    </div>
  );
};
